// SignupOwner.js
import { getDatabase, ref, set, update } from "firebase/database";

import { useAuth } from "../contexts/AuthContext";
import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import Cropper, { ReactCropperElement } from "react-cropper"; // Import Cropper component and types
import "cropperjs/dist/cropper.css"; // Import Cropper CSS
import {
  Button,
  Form,
  Modal,
  ProgressBar,
  Row,
  Col,
  Alert,
} from "react-bootstrap";
import {
  getAllOwnerSelectionQuestions,
  getAllOwnerTextQuestions,
  getPlatformPercentages,
  newUserAddedAdminEmailBody,
  sendEmail,
  sendEmailWithRetry,
  sendSMS,
  welcomeEmailBody,
  welcomeOwnerEmailBody,
  getAllReferralCodesWithOwners,
  calculateAgeFromDateOfBirth,
} from "./utils";
import { notifyNewBoatOwner } from "./utils/adminNotifications";

const isTaxIdentifierQuestion = (question = "") => {
  const normalizedQuestion = question.trim().toLowerCase();

  return (
    normalizedQuestion.includes("social security") ||
    normalizedQuestion.includes("ssn") ||
    normalizedQuestion.includes("ein") ||
    normalizedQuestion.includes("tax id") ||
    normalizedQuestion.includes("tax identification") ||
    normalizedQuestion.includes("taxpayer identification") ||
    normalizedQuestion.includes("xxx-xx-xxxx")
  );
};

const SignupOwner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(1);

  const cropperRef = useRef(null); // Create a ref to the Cropper component
  const [croppedImage, setCroppedImage] = useState(null);

  const [isModalOpen, setModalOpen] = useState(false);

  const openModal = () => {
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  useEffect(() => {
    console.log(croppedImage);
  }, [croppedImage]);
  const serverUrl = process.env.REACT_APP_BASE_URL;

  const queryParams = new URLSearchParams(location.search);
  const refParam = queryParams.get("ref");

  const { signup, refreshUserData, setIsSigningUp } = useAuth();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [phoneNo, setPhoneNo] = useState("");
  const [textQuestionAnswers, setTextQuestionAnswers] = useState([]);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    referralCode: refParam || "",
    password: "",
    avatar: "",
    dateOfBirth: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zipCode: "",
    country: "",
  });

  const [multiChoiceQuestions, setMultiChoiceQuestions] = useState([]);
  const [textQuestions, setTextQuestions] = useState([]);
  const [referralCodes, setReferralCodes] = useState([]);
  const [filteredReferralCodes, setFilteredReferralCodes] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const questionsData = await getAllOwnerSelectionQuestions();
        // Convert the object into an array of values
        const questionsArray = Object.values(questionsData);
        setMultiChoiceQuestions(questionsArray);
        // Convert the object into an array of values and filter out Address Line 2
        const textQuestionsData = await getAllOwnerTextQuestions();
        // Filter out address fields (collected in step 1) and tax ID questions (handled via Stripe)
        const testQuestionsArray = Object.values(textQuestionsData).filter(
          (question) => {
            const q = question.trim().toLowerCase();
            return (
              ![
                "address line 1",
                "address line 2",
                "city",
                "state/province",
                "zip code",
                "country",
              ].includes(q) && !isTaxIdentifierQuestion(question)
            );
          }
        );
        console.log(testQuestionsArray);
        setTextQuestions(testQuestionsArray);
      } catch (error) {
        console.error("Error fetching Questions:", error);
      }
    };

    fetchQuestions();
  }, []);

  useEffect(() => {
    const fetchReferralCodes = async () => {
      try {
        const codes = await getAllReferralCodesWithOwners();
        setReferralCodes(codes);
        setFilteredReferralCodes(codes);
      } catch (error) {
        console.error("Error fetching referral codes:", error);
      }
    };

    fetchReferralCodes();
  }, []);

  // Handle pre-filled referral code from URL
  useEffect(() => {
    if (refParam && referralCodes.length > 0) {
      const matchingCode = referralCodes.find(code => code.code === refParam);
      if (matchingCode) {
        setSearchTerm(matchingCode.displayText);
      } else {
        setSearchTerm(refParam);
      }
    }
  }, [refParam, referralCodes]);

  // Handle referral code search
  const handleReferralSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setFormData({ ...formData, referralCode: value });

    // Filter referral codes based on search term
    const filtered = referralCodes.filter(code =>
      code.displayText.toLowerCase().includes(value.toLowerCase()) ||
      code.code.toLowerCase().includes(value.toLowerCase()) ||
      code.ownerName.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredReferralCodes(filtered);
    setShowDropdown(value.length > 0 && filtered.length > 0);
  };

  // Handle referral code selection
  const handleReferralSelect = (selectedCode) => {
    setFormData({ ...formData, referralCode: selectedCode.code });
    setSearchTerm(selectedCode.displayText);
    setShowDropdown(false);
  };

  // Handle input focus
  const handleReferralFocus = () => {
    if (filteredReferralCodes.length > 0) {
      setShowDropdown(true);
    }
  };

  // Handle input blur (with delay to allow for clicks)
  const handleReferralBlur = () => {
    setTimeout(() => setShowDropdown(false), 200);
  };

  const handleChange = (e, question) => {
    const { name, value } = e.target;

    // Clear validation error for this field when user starts typing
    if (validationErrors[name]) {
      setValidationErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors[name];
        return newErrors;
      });
    }

    if (name.startsWith("question_")) {
      // Update textfield answers
      const questionIndex = parseInt(name.split("_")[1], 10);
      const existingAnswerIndex = textQuestionAnswers.findIndex(
        (answer) => answer.questionText === textQuestions[questionIndex]
      );

      // Clear validation error for this question
      if (validationErrors[`question_${questionIndex}`]) {
        setValidationErrors((prevErrors) => {
          const newErrors = { ...prevErrors };
          delete newErrors[`question_${questionIndex}`];
          return newErrors;
        });
      }

      if (existingAnswerIndex !== -1) {
        // If an answer exists, update the existing answer
        setTextQuestionAnswers((prevAnswers) => {
          const updatedAnswers = [...prevAnswers];
          updatedAnswers[existingAnswerIndex] = {
            questionText: textQuestions[questionIndex],
            answer: value,
          };
          return updatedAnswers;
        });
      } else {
        // If no answer exists, add a new answer to the array
        setTextQuestionAnswers((prevAnswers) => [
          ...prevAnswers,
          {
            questionText: textQuestions[questionIndex],
            answer: value,
          },
        ]);
      }
    } else {
      // Update other form data
      setFormData((prevData) => ({ ...prevData, [name]: value }));
    }
    console.log(textQuestionAnswers);
  };

  // Validation function for step 1
  const validateStep1 = () => {
    const errors = {};

    if (!formData.firstName.trim()) {
      errors.firstName = "First name is required";
    }

    if (!formData.lastName.trim()) {
      errors.lastName = "Last name is required";
    }

    const age = calculateAgeFromDateOfBirth(formData.dateOfBirth);
    if (!formData.dateOfBirth || age === null || age < 18) {
      errors.dateOfBirth = "Date of birth is required and you must be 18 or older";
    }

    if (!phoneNo.trim()) {
      errors.phone = "Phone number is required";
    }

    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Please enter a valid email address";
    }

    if (!formData.password.trim()) {
      errors.password = "Password is required";
    } else if (formData.password.length < 6) {
      errors.password = "Password must be at least 6 characters";
    }

    if (!formData.address1.trim()) {
      errors.address1 = "Address Line 1 is required";
    }

    if (!formData.city.trim()) {
      errors.city = "City is required";
    }

    if (!formData.state.trim()) {
      errors.state = "State/Province is required";
    }

    if (!formData.zipCode.trim()) {
      errors.zipCode = "Zip Code is required";
    }

    if (!formData.country.trim()) {
      errors.country = "Country is required";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Validation function for step 2
  const validateStep2 = () => {
    const errors = {};

    textQuestions.forEach((question, index) => {
      const answer = textQuestionAnswers.find(
        (answer) => answer.questionText === question
      );
      if (!answer || !answer.answer.trim()) {
        errors[`question_${index}`] = "This field is required";
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  async function handleSubmit(e) {
    e.preventDefault();

    try {
      setIsSigningUp(true);
      setError("");
      setLoading(true);

      const params = new URLSearchParams(window.location.search);
      const redirectTo = params.get("redirect_to");

      // Signup the user using email and password
      const userCredential = await signup(formData.email, formData.password);
      const userId = userCredential.user.uid; // Assuming Firebase auth returns a user object with uid

      const platformSettings = await getPlatformPercentages();
      const userSuffixNew = parseInt(platformSettings.userSuffix) + 1;
      const bbrID = "BBR" + userSuffixNew;

      const setLastSuffix = ref(getDatabase(), `portalSettings/settingsID`);
      await update(setLastSuffix, {
        userSuffix: userSuffixNew,
      });

      console.log("bbrID");
      console.log(bbrID);

      // Store user information in the Realtime Database under 'users' node
      const userRef = ref(getDatabase(), `users/${userId}`);
      await set(userRef, {
        bbrId: bbrID,
        firstName: formData.firstName,
        lastName: formData.lastName,
        avatar: formData.avatar,
        refferalCode: (() => {
          const raw = (formData.referralCode || "").trim();
          if (!raw) return "";
          const matchedCode = referralCodes.find(
            (code) =>
              code.code.toUpperCase() === raw.toUpperCase() ||
              code.displayText === raw
          );
          return matchedCode ? matchedCode.code : raw.toUpperCase();
        })(),
        email: formData.email,
        phone: phoneNo,
        role: "Boat Owner",
        status: "Active",
        address1: formData.address1,
        address2: formData.address2,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
        country: formData.country,
      });
      const ownerMetaRef = ref(getDatabase(), `ownerMeta/${userId}`);

      await set(ownerMetaRef, {
        payments: {
          currentAmount: 0,
          totalAmount: 0,
        },
        totalPaidBookings: 0,
      });
      // Store userMeta information under 'userMeta' node with the same user ID
      const userAge = calculateAgeFromDateOfBirth(formData.dateOfBirth);
      const userMetaRef = ref(getDatabase(), `userMeta/${userId}`);
      await set(userMetaRef, {
        dateOfBirth: formData.dateOfBirth,
        age: userAge,
        userAnswers: {
          // Assuming multiChoiceQuestions and textQuestionAnswers are arrays of objects
          multiChoiceQuestions: multiChoiceQuestions.map((question, index) => ({
            question: question.questionText,
            answer: selectedOptions[index]?.option || "", // Assuming selectedOptions is an array of selected options
          })),
          textQuestions: textQuestions.map((question, index) => ({
            question: question,
            answer: textQuestionAnswers[index]?.answer || "",
          })),
        },
        // Add other userMeta data fields as needed
      });

      const userFullName = formData.firstName + " " + formData.lastName;
      const subject = "Welcome Aboard! " + userFullName;
      const to = formData.email;

      const emailBodyNew = await welcomeOwnerEmailBody(userFullName);
      const emailSent = await sendEmailWithRetry(to, subject, emailBodyNew);

      if (!emailSent) {
        console.error("Failed to send welcome email to:", to);
        setError("Welcome email could not be sent. Please check your email address and try again.");
        return;
      } else {
        console.log("Welcome email sent successfully to:", to);
      }

      const smsBody = `Welcome Aboard ${userFullName}, your account has been registered successfully.\n\n${process.env.REACT_APP_BASE_URL}/auth`;

      // Send SMS (non-blocking - failure won't prevent signup)
      const smsResponse = await sendSMS(formData.phone, smsBody);
      if (smsResponse.success) {
        console.log("SMS sent successfully:", smsResponse.data);
      } else {
        console.log("SMS not sent:", smsResponse.message);
      }

      const adminEmailBody = await newUserAddedAdminEmailBody(
        "Boat Owner",
        bbrID,
        userFullName,
        to
      );
      const adminEmailSent = await sendEmail(
        "waivers@bbrboatrentals.com",
        "New Boat Owner Registered.",
        adminEmailBody
      );

      if (!adminEmailSent) {
        console.error("Failed to send admin notification email");
      } else {
        console.log("Admin notification email sent successfully");
      }

      notifyNewBoatOwner(userFullName).catch((err) =>
        console.error("Failed to create admin notification:", err)
      );

      await refreshUserData();

      if (redirectTo) {
        if (redirectTo.startsWith('/')) {
          navigate(redirectTo);
        } else {
          navigate(`/listing/${redirectTo}`);
        }
      } else {
        window.location.href = "/dashboard";
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setIsSigningUp(false);
      setLoading(false);
    }
  }

  const [avatar, setAvatar] = useState(null);

  const onCrop = () => {
    const cropper = cropperRef.current.cropper; // Access the Cropper instance through the ref
    setCroppedImage(cropper.getCroppedCanvas().toDataURL());
  };

  const handleUploadAvatar = async () => {
    if (croppedImage) {
      // Convert the base64 image to a Blob
      const blob = await fetch(croppedImage).then((res) => res.blob());
      console.log(blob);
      // Create FormData and append the file
      const formData = new FormData();
      formData.append("file", blob);

      try {
        const response = await fetch(`${serverUrl}/upload-featured-image`, {
          method: "POST",
          body: formData,
        });

        if (response.ok) {
          const responseData = await response.json();
          setFormData((prevUserValues) => ({
            ...prevUserValues,
            avatar: `${serverUrl}/images/${responseData.file_name}`,
          }));
          setAvatar(null);
        } else {
          console.error("File upload failed");
        }
      } catch (error) {
        console.error("Error uploading file:", error);
      }
    } else {
      console.error("No cropped image available");
    }
  };

  const handleSelectImage = (e) => {
    const file = e.target.files[0];
    const reader = new FileReader();
    reader.onload = () => {
      setAvatar(reader.result); // Set the file URL as the avatar
    };
    reader.readAsDataURL(file); // Convert the file to a data URL
  };

  const nextStep = () => {
    if (step === 1) {
      if (validateStep1()) {
        setStep((prevStep) => prevStep + 1);
        // Clear validation errors when moving to next step
        setValidationErrors({});
      }
    } else {
      setStep((prevStep) => prevStep + 1);
    }
  };

  const prevStep = () => {
    setStep((prevStep) => prevStep - 1);
    // Clear validation errors when going back to previous step
    setValidationErrors({});
  };

  const [selectedOptions, setSelectedOptions] = useState([]);

  const handleRadioChange = (questionText, option) => {
    // Check if an option for the same question already exists
    const existingOptionIndex = selectedOptions.findIndex(
      (selectedOption) => selectedOption.questionText === questionText
    );

    if (existingOptionIndex !== -1) {
      // If an option exists, update the existing option
      setSelectedOptions((prevOptions) => {
        const updatedOptions = [...prevOptions];
        updatedOptions[existingOptionIndex] = {
          questionText,
          option,
        };
        return updatedOptions;
      });
    } else {
      // If no option exists, add a new option to the array
      setSelectedOptions((prevOptions) => [
        ...prevOptions,
        {
          questionText,
          option,
        },
      ]);
    }
  };

  const [redirectTo, setRedirectTo] = useState("");
  const [redirectId, setRedirectId] = useState("");
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const redirectTo = params.get("redirect_to");
    if (redirectTo) {
      setRedirectTo(`?redirect_to=${redirectTo}`);
      setRedirectId(redirectTo);
    }
  }, []);

  return (
    <>
      <p className="auth-description">
        Please enter your credentials to create an account.
        <br />
        Already have an account? <Link to={`/auth${redirectTo}`}>Sign In</Link>
      </p>

      <div className="auth-credentials m-b-xxl">
        <Row>
          <Col>
            <ProgressBar now={(step / 2) * 100} className="mb-3" />
            <Form onSubmit={handleSubmit}>
              {error && <Alert variant="danger">{error}</Alert>}
              {step === 1 && (
                <>
                  <Form.Group controlId="firstName" className="mt-4">
                    <Row className="mb-2">
                      <Col xs={6}>
                        <Form.Label className="form-label">
                          First Name
                          <span style={{ color: "red" }}> *</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          className="form-control"
                          style={{
                            border: validationErrors.firstName ? "2px solid #dc3545" : "2px solid #c8c8c8",
                          }}
                          required
                        />
                        {validationErrors.firstName && (
                          <div style={{ color: "#dc3545", fontSize: "0.875rem", marginTop: "0.25rem" }}>
                            {validationErrors.firstName}
                          </div>
                        )}
                      </Col>
                      <Col xs={6}>
                        <Form.Label>
                          Last Name<span style={{ color: "red" }}> *</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                          className="form-control"
                          style={{
                            border: validationErrors.lastName ? "2px solid #dc3545" : "2px solid #c8c8c8",
                          }}
                          required
                        />
                        {validationErrors.lastName && (
                          <div style={{ color: "#dc3545", fontSize: "0.875rem", marginTop: "0.25rem" }}>
                            {validationErrors.lastName}
                          </div>
                        )}
                      </Col>
                    </Row>

                    <Row className="mb-2">
                      <Col xs={12}>
                        <Form.Label className="form-label">
                          Profile Picture{" "}
                          <span style={{ color: "gray", fontSize: "0.9em" }}>
                            (optional)
                          </span>
                        </Form.Label>
                        <Form.Control
                          type="file"
                          name="avatar"
                          onChange={(e) => handleSelectImage(e)}
                          accept="image/*"
                        />
                        {avatar && (
                          <>
                            <Cropper
                              src={avatar} // Image source
                              className="mt-3"
                              style={{ height: 400, width: "100%" }} // Styling for Cropper container
                              initialAspectRatio={16 / 9} // Initial aspect ratio for cropping
                              guides={false} // Display cropping guides
                              crop={onCrop} // Callback function triggered after cropping
                              ref={cropperRef} // Assign the ref to the Cropper component
                            />
                            <Button
                              className="mt-3"
                              onClick={handleUploadAvatar}
                            >
                              Finish Cropping
                            </Button>
                          </>
                        )}
                      </Col>
                    </Row>

                    <Row className="mb-2">
                      <Col xs={6}>
                        <Form.Label>
                          Date of Birth<span style={{ color: "red" }}> *</span>
                        </Form.Label>
                        <Form.Control
                          type="date"
                          name="dateOfBirth"
                          value={formData.dateOfBirth}
                          className="form-control"
                          onChange={handleChange}
                          max={new Date().toISOString().split("T")[0]}
                          style={{
                            border: validationErrors.dateOfBirth ? "2px solid #dc3545" : "2px solid #c8c8c8",
                          }}
                          required
                        />
                        {validationErrors.dateOfBirth && (
                          <div style={{ color: "#dc3545", fontSize: "0.875rem", marginTop: "0.25rem" }}>
                            {validationErrors.dateOfBirth}
                          </div>
                        )}
                      </Col>
                      <Col xs={6}>
                        <Form.Label>
                          Phone<span style={{ color: "red" }}> *</span>
                        </Form.Label>
                        <PhoneInput
                          country={"us"}
                          value={phoneNo}
                          onChange={(value) => {
                            setPhoneNo(value);
                            // Clear validation error when user types
                            if (validationErrors.phone) {
                              setValidationErrors((prevErrors) => {
                                const newErrors = { ...prevErrors };
                                delete newErrors.phone;
                                return newErrors;
                              });
                            }
                          }}
                          inputStyle={{
                            width: "100%",
                            height: "38px",
                            border: validationErrors.phone ? "2px solid #dc3545" : "2px solid #c8c8c8",
                          }}
                        />
                        {validationErrors.phone && (
                          <div style={{ color: "#dc3545", fontSize: "0.875rem", marginTop: "0.25rem" }}>
                            {validationErrors.phone}
                          </div>
                        )}
                      </Col>
                    </Row>

                    <Row className="mb-2">
                      <Col xs={6}>
                        <Form.Label>
                          Email<span style={{ color: "red" }}> *</span>
                        </Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className="form-control"
                          style={{
                            border: validationErrors.email ? "2px solid #dc3545" : "2px solid #c8c8c8",
                          }}
                          required
                        />
                        {validationErrors.email && (
                          <div style={{ color: "#dc3545", fontSize: "0.875rem", marginTop: "0.25rem" }}>
                            {validationErrors.email}
                          </div>
                        )}
                      </Col>
                      <Col xs={6}>
                        <Form.Label>
                          Password<span style={{ color: "red" }}> *</span>
                        </Form.Label>
                        <div style={{ position: "relative", width: "100%" }}>
                          <Form.Control
                            type={showPassword ? "text" : "password"}
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            className="form-control"
                            required
                            style={{
                              paddingRight: "2.5rem",
                              border: validationErrors.password ? "2px solid #dc3545" : "2px solid #c8c8c8",
                            }}
                          />
                          <button
                            type="button"
                            aria-label={
                              showPassword ? "Hide password" : "Show password"
                            }
                            onClick={() => setShowPassword((prev) => !prev)}
                            style={{
                              position: "absolute",
                              right: "0.5rem",
                              top: "50%",
                              transform: "translateY(-50%)",
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              padding: 0,
                              height: "2rem",
                              width: "2rem",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {showPassword ? (
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M17.94 17.94A10.94 10.94 0 0 1 12 19c-5 0-9.27-3.11-11-7 1.21-2.61 3.31-4.77 6-6.13" />
                                <path d="M1 1l22 22" />
                                <path d="M9.53 9.53A3.5 3.5 0 0 0 12 16.5c1.38 0 2.63-.56 3.54-1.47" />
                                <path d="M14.47 14.47A3.5 3.5 0 0 1 12 7.5c-.88 0-1.68.32-2.32.84" />
                              </svg>
                            ) : (
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                <circle cx="12" cy="12" r="3" />
                              </svg>
                            )}
                          </button>
                        </div>
                        {validationErrors.password && (
                          <div style={{ color: "#dc3545", fontSize: "0.875rem", marginTop: "0.25rem" }}>
                            {validationErrors.password}
                          </div>
                        )}
                      </Col>
                    </Row>
                    <Row className="mb-2">
                      <Col xs={12}>
                        <Form.Label>Have a Referral code?</Form.Label>
                        <div style={{ position: 'relative' }}>
                          <Form.Control
                            type="text"
                            name="referralCode"
                            value={searchTerm || formData.referralCode}
                            onChange={handleReferralSearch}
                            onFocus={handleReferralFocus}
                            onBlur={handleReferralBlur}
                            placeholder="Choose the awesome pro staff member that referred you"
                            className="form-control"
                          />
                          {showDropdown && (
                            <div
                              style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                right: 0,
                                backgroundColor: 'white',
                                border: '1px solid #ced4da',
                                borderTop: 'none',
                                borderRadius: '0 0 0.375rem 0.375rem',
                                maxHeight: '200px',
                                overflowY: 'auto',
                                zIndex: 1000,
                                boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                              }}
                            >
                              {filteredReferralCodes.map((code, index) => (
                                <div
                                  key={index}
                                  onClick={() => handleReferralSelect(code)}
                                  style={{
                                    padding: '8px 12px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #f1f1f1',
                                    transition: 'background-color 0.2s'
                                  }}
                                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                                  onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                                >
                                  <div style={{ fontWeight: 'bold', color: '#495057' }}>
                                    {code.code}
                                  </div>
                                  <div style={{ fontSize: '0.875rem', color: '#6c757d' }}>
                                    {code.ownerName}
                                  </div>
                                </div>
                              ))}
                              {filteredReferralCodes.length === 0 && searchTerm && (
                                <div style={{ padding: '8px 12px', color: '#6c757d', fontStyle: 'italic' }}>
                                  No referral codes found
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        <small className="text-muted">
                          Search by referral code or owner name. Leave empty if you don't have a referral code.
                        </small>
                      </Col>
                    </Row>
                  </Form.Group>

                  <Row className="mb-2">
                    <Col xs={12}>
                      <Form.Label>Address Line 1<span style={{ color: 'red' }}> *</span></Form.Label>
                      <Form.Control
                        type="text"
                        name="address1"
                        value={formData.address1}
                        onChange={handleChange}
                        className="form-control"
                        style={{
                          border: validationErrors.address1 ? "2px solid #dc3545" : "2px solid #c8c8c8",
                        }}
                        required
                      />
                      {validationErrors.address1 && (
                        <div style={{ color: "#dc3545", fontSize: "0.875rem", marginTop: "0.25rem" }}>
                          {validationErrors.address1}
                        </div>
                      )}
                    </Col>
                  </Row>

                  <Row className="mb-2">
                    <Col xs={12}>
                      <Form.Label>Address Line 2 <span style={{ color: 'gray', fontSize: '0.9em' }}>(optional)</span></Form.Label>
                      <Form.Control
                        type="text"
                        name="address2"
                        value={formData.address2}
                        onChange={handleChange}
                        className="form-control"
                      />
                    </Col>
                  </Row>

                  <Row className="mb-2">
                    <Col xs={3}>
                      <Form.Label>City<span style={{ color: 'red' }}> *</span></Form.Label>
                      <Form.Control
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        className="form-control"
                        style={{
                          border: validationErrors.city ? "2px solid #dc3545" : "2px solid #c8c8c8",
                        }}
                        required
                      />
                      {validationErrors.city && (
                        <div style={{ color: "#dc3545", fontSize: "0.875rem", marginTop: "0.25rem" }}>
                          {validationErrors.city}
                        </div>
                      )}
                    </Col>
                    <Col xs={3}>
                      <Form.Label>State/Province<span style={{ color: 'red' }}> *</span></Form.Label>
                      <Form.Control
                        type="text"
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        className="form-control"
                        style={{
                          border: validationErrors.state ? "2px solid #dc3545" : "2px solid #c8c8c8",
                        }}
                        required
                      />
                      {validationErrors.state && (
                        <div style={{ color: "#dc3545", fontSize: "0.875rem", marginTop: "0.25rem" }}>
                          {validationErrors.state}
                        </div>
                      )}
                    </Col>
                    <Col xs={3}>
                      <Form.Label>Zip Code<span style={{ color: 'red' }}> *</span></Form.Label>
                      <Form.Control
                        type="text"
                        name="zipCode"
                        value={formData.zipCode}
                        onChange={handleChange}
                        className="form-control"
                        style={{
                          border: validationErrors.zipCode ? "2px solid #dc3545" : "2px solid #c8c8c8",
                        }}
                        required
                      />
                      {validationErrors.zipCode && (
                        <div style={{ color: "#dc3545", fontSize: "0.875rem", marginTop: "0.25rem" }}>
                          {validationErrors.zipCode}
                        </div>
                      )}
                    </Col>
                    <Col xs={3}>
                      <Form.Label>Country<span style={{ color: 'red' }}> *</span></Form.Label>
                      <Form.Control
                        type="text"
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        className="form-control"
                        style={{
                          border: validationErrors.country ? "2px solid #dc3545" : "2px solid #c8c8c8",
                        }}
                        required
                      />
                      {validationErrors.country && (
                        <div style={{ color: "#dc3545", fontSize: "0.875rem", marginTop: "0.25rem" }}>
                          {validationErrors.country}
                        </div>
                      )}
                    </Col>
                  </Row>

                  <Row className="mt-4">
                    <Col>
                      <Button variant="btn btn-primary" onClick={nextStep}>
                        Next
                      </Button>
                    </Col>
                  </Row>
                </>
              )}
              {/* {step === 2 && (
          <>
          {multiChoiceQuestions.map((question, questionIndex) => (
            <Form.Group key={questionIndex}>
              <Form.Label className='mt-2'>{question.questionText}</Form.Label>
              <div className='d-flex'>
                {question.options.map((option, optionIndex) => (
                  <div key={optionIndex} className="mb-4">
                    <Form.Check
                      type="radio"
                      style={{textAlign: 'start'}}
                      label={option}
                      className="btn btn-light m-1"
                      name={`question_${questionIndex}`}
                      id={`option_${questionIndex}_${optionIndex}`}
                      onChange={() => handleRadioChange(question.questionText, option)}
                    />
                  </div>
                ))}
              </div>
            </Form.Group>
          ))}
          <Row className='mt-4'>
            <Col>
              <Button className='mx-2' variant="secondary" onClick={prevStep}>Previous</Button>
              <Button className='mx-2' variant="primary" onClick={nextStep}>Next</Button>
            </Col>
          </Row>
          
        </>
        )} */}
              {step === 2 && (
                <>
                  {textQuestions.map((question, questionIndex) => (
                    <Form.Group key={questionIndex} className="mt-4">
                      <Form.Label>
                        {question}
                        <span style={{ color: "red" }}> *</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name={`question_${questionIndex}`}
                        value={
                          textQuestionAnswers.find(
                            (answer) => answer.questionText === question
                          )?.answer || ""
                        }
                        onChange={(e) => handleChange(e, question)}
                        required={true}
                        style={{
                          border: validationErrors[`question_${questionIndex}`] ? "2px solid #dc3545" : "2px solid #c8c8c8",
                        }}
                      />
                      {validationErrors[`question_${questionIndex}`] && (
                        <div style={{ color: "#dc3545", fontSize: "0.875rem", marginTop: "0.25rem" }}>
                          {validationErrors[`question_${questionIndex}`]}
                        </div>
                      )}
                    </Form.Group>
                  ))}
                  <Row>
                    <Col>
                      <Form.Check
                        type="checkbox"
                        style={{ marginTop: "20px" }}
                        label={
                          <>
                            By signing up for BBR Bass Boat Rentals, users agree
                            to abide by these
                            <span
                              onClick={openModal}
                              style={{
                                marginLeft: "5px",
                                marginRight: "5px",
                                textDecoration: "underline",
                                color: "#0d5ceb",
                                cursor: "pointer",
                              }}
                            >
                              rules
                            </span>
                            and contribute to a safe, fair, and trusted
                            community of boat renters and owners.
                          </>
                        }
                        required
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <div className="auth-submit mt-4">
                        <Button
                          className="mx-2"
                          variant="secondary"
                          onClick={prevStep}
                        >
                          Previous
                        </Button>
                        <Button
                          className="mx-2"
                          variant="primary"
                          type="submit"
                          disabled={loading}
                          onClick={(e) => {
                            if (!validateStep2()) {
                              e.preventDefault();
                            }
                          }}
                        >
                          {loading ? "Creating Account..." : "Submit"}
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </>
              )}
            </Form>
          </Col>
        </Row>

        <Modal centered show={isModalOpen} onHide={closeModal}>
          <Modal.Header closeButton>
            <Modal.Title>
              BBR Bass Boat Rentals - Sign Up Rules and Guidelines
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <h4>1. Communication Within the BBR Platform Only</h4>
            <p>
              All communication between boat owners and renters must take place
              exclusively within the BBR Bass Boat Rentals dashboard. Sharing
              personal phone numbers, emails, or any other form of contact
              information for the purpose of arranging bookings outside of the
              system is strictly prohibited.
            </p>
            <h5>Reason:</h5>
            <ul>
              <li>
                <strong>Fair Business Practices:</strong> BBR’s platform is
                designed to ensure that transactions and bookings are
                transparent, secure, and fairly conducted. Making bookings
                outside of the system compromises the fairness and integrity of
                the platform.
              </li>
              <li>
                <strong>Safety in Communications:</strong> By keeping all
                communication within the dashboard, BBR can ensure that any
                issues or disputes can be resolved fairly. It also protects the
                privacy of both parties by keeping personal contact details
                confidential.
              </li>
              <li>
                <strong>Liability Protection:</strong> BBR provides legal
                coverage and waivers to both renters and boat owners through the
                platform. Any transaction or communication outside of the system
                could lead to liability issues and will not be covered under
                BBR's policies.
              </li>
            </ul>
            <h4>2. No Off-Platform Transactions</h4>
            <p>
              All bookings, payments, and transactions must be completed through
              the BBR Bass Boat Rentals system. Any attempt to circumvent this
              process by making off-platform deals or payments will result in
              suspension or termination of the account.
            </p>
            <h5>Reason:</h5>
            <ul>
              <li>
                This ensures that the platform can properly track, secure, and
                guarantee each booking.
              </li>
              <li>
                Off-platform transactions undermine the protection offered by
                BBR’s system, including payment security, insurance, and
                liability coverage.
              </li>
            </ul>
            <h4>3. Adherence to BBR's Safety Standards</h4>
            <p>
              By signing up, users agree to adhere to the safety guidelines and
              standards set by BBR Bass Boat Rentals, which include following
              local boating laws and regulations, providing accurate information
              about boat conditions, and using the appropriate safety equipment.
            </p>
            <h4>4. Liability Waivers</h4>
            <p>
              All users must acknowledge that BBR Bass Boat Rentals provides
              liability waivers to both renters and boat owners. These waivers
              are only valid for transactions made through the platform. BBR
              cannot guarantee or cover any damages, losses, or issues arising
              from bookings made outside of the platform.
            </p>
            <h5>Reason:</h5>
            <ul>
              <li>
                The liability waivers ensure that both parties are legally
                protected during the rental period, but this protection is only
                available for on-platform transactions.
              </li>
            </ul>
            <h4>5. Community of Trust</h4>
            <p>
              BBR Bass Boat Rentals strives to create a community built on trust
              and mutual respect. By following these rules, users contribute to
              a safe and reliable environment for everyone involved. Maintaining
              the integrity of the platform helps boat owners feel secure about
              renting their boats, and it assures renters that they are booking
              from verified and trustworthy owners.
            </p>
            <h4>6. Violations of Terms</h4>
            <p>
              Any violation of these rules, including attempts to circumvent the
              platform, will result in immediate action, which may include
              suspension or permanent removal from the platform.
            </p>
            <p style={{ marginTop: "20px" }}>
              <strong>
                By signing up for BBR Bass Boat Rentals, users agree to abide by
                these rules and contribute to a safe, fair, and trusted
                community of boat renters and owners.
              </strong>
            </p>
          </Modal.Body>
        </Modal>
      </div>
    </>
  );
};

export default SignupOwner;
